import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { fetchBookingLifecycle } from '../../../../store/slices/adminSlice';

/**
 * BookingLifecycleModal displays a vertical timeline of a booking's history.
 * It follows the design patterns seen in the JetSetter Admin Dashboard.
 */
const BookingLifecycleModal = ({ isOpen, onClose, booking }) => {
  const dispatch = useAppDispatch();
  const { bookingLifecycle: lifecycle, lifecycleLoading, lifecycleError } = useAppSelector((state) => state.admin);

  useEffect(() => {
    if (isOpen && booking?.booking_token) {
      dispatch(fetchBookingLifecycle(booking.booking_token));
    }
  }, [isOpen, booking, dispatch]);

  if (!isOpen) return null;

  // Helper to determine icon and color based on event type
  const getIconConfig = (type) => {
    const eventType = type?.toLowerCase() || '';
    if (eventType.includes('created')) {
      return { icon: 'fa-calendar-check', color: 'bg-[#28a745]' }; // Green
    } else if (eventType.includes('payment')) {
      return { icon: 'fa-credit-card', color: 'bg-[#007bff]' }; // Blue
    } else if (eventType.includes('confirmed') || eventType.includes('completed')) {
      return { icon: 'fa-plane-circle-check', color: 'bg-[#6c757d]' }; // Gray/Secondary
    } else if (eventType.includes('cancel')) {
      return { icon: 'fa-ban', color: 'bg-[#dc3545]' }; // Red
    }
    return { icon: 'fa-bell', color: 'bg-[#6c757d]' }; // Default Gray
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 font-['Bahnschrift',sans-serif]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header - Consistent with Admin Blue Header */}
        <div className="bg-[#03538f] px-4 py-3 flex items-center justify-between">
          <h3 className="text-white text-md font-medium flex items-center gap-2 m-0">
            <i className="fas fa-history"></i>
            Booking Lifecycle — {booking?.reference_number || 'N/A'}
          </h3>
          <button 
            onClick={onClose} 
            className="text-white hover:text-gray-300 transition-colors focus:outline-none"
            aria-label="Close"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Content Area */}
        <div className="p-4 max-h-[60vh] overflow-y-auto bg-gray-50/30">
          {lifecycleLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-[#03538f] mb-4"></div>
              <p className="text-gray-500 text-sm">Retrieving lifecycle data...</p>
            </div>
          ) : lifecycleError ? (
            <div className="text-center py-16 px-6">
              <i className="fas fa-exclamation-triangle text-4xl text-red-400 mb-4 opacity-80"></i>
              <p className="text-gray-700 font-medium">Failed to load booking history</p>
              <p className="text-red-500 text-xs mt-1">{lifecycleError}</p>
            </div>
          ) : !lifecycle || lifecycle.length === 0 ? (
            <div className="text-center py-20 opacity-40">
              <i className="fas fa-timeline text-6xl mb-4 text-gray-300"></i>
              <p className="text-gray-500">No events recorded for this booking yet.</p>
            </div>
          ) : (
            <div className="relative px-2">
              {/* Continuous Vertical Connector Line */}
              <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-200"></div>

              {/* Timeline Items */}
              <div className="space-y-6">
                {(() => {
                  // Filter out duplicate cancellation events - keep only the first "Flight Cancelled" event
                  const filteredLifecycle = lifecycle.filter((event, index) => {
                    const title = event.title?.toLowerCase() || '';
                    
                    // If it's a cancellation-related event
                    if (title.includes('cancel') || title.includes('fee')) {
                      // Find the first cancellation event
                      const firstCancelIndex = lifecycle.findIndex(e => 
                        e.title?.toLowerCase().includes('cancel') || e.title?.toLowerCase().includes('fee')
                      );
                      
                      // Only keep the first cancellation event, skip others
                      if (title.includes('flight cancelled') && !title.includes('fee')) {
                        return index === firstCancelIndex;
                      } else if (title.includes('fee') || title.includes('charged')) {
                        return false; // Skip all fee-related events
                      }
                    }
                    
                    return true; // Keep all other events
                  });

                  return filteredLifecycle.map((event, index) => {
                  const { icon, color } = getIconConfig(event.type);
                  return (
                    <div key={index} className="relative flex items-start gap-4 group">
                      {/* Status Icon with Inner Glow */}
                      <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-110 ${color}`}>
                        <i className={`fas ${icon} text-xs`}></i>
                      </div>

                      {/* Label Card */}
                      <div className="flex-1 bg-white border border-gray-200 rounded-lg p-3 shadow-sm relative before:content-[''] before:absolute before:left-[-6px] before:top-2 before:w-3 before:h-3 before:bg-white before:border-l before:border-b before:border-gray-200 before:rotate-45">
                        <h4 className="text-sm font-bold text-gray-800 m-0 mb-1">{event.title}</h4>
                        <p className="text-xs text-gray-600 m-0 mb-2 leading-relaxed font-light">
                          {event.description}
                        </p>
                        
                        {/* Timestamp with Clock Icon */}
                        <div className="flex items-center gap-2 text-[10px] text-gray-400 border-t border-gray-50 pt-2">
                          <i className="far fa-clock"></i>
                          <span className="font-medium tracking-tight">
                            {event.timestamp || (event.created_at ? new Date(event.created_at).toLocaleString('en-US', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                            }) : 'N/A')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Action Footer */}
        <div className="px-4 py-3 flex justify-end border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-900 transition-all focus:ring-2 focus:ring-gray-200 active:scale-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingLifecycleModal;
